/**
 * HOC cannot have stories by virtue of being HOC.
 */
/* eslint-disable local-rules/require-storybook-documentation */
import dynamic from 'next/dynamic';
import { FC } from 'react';
import { getComponentDisplayName } from 'src/utils/getters/getComponentDisplayName';

import { LoginHintProps } from 'src/common/types';
import { SessionWatcher } from '../SessionWatcher';
import { SessionStatus } from '../../constants';
import { useWithAuthState } from './hooks/useWithAuthState';
import { ServerSideSessionProps } from './GetServerSideSessionProps';

type WithAuthConfiguration = {
    forceAuth?: boolean;
};

const DEFAULT_WITH_AUTH_OPTIONS: Required<WithAuthConfiguration> = {
    forceAuth: false,
};

const AuthenticationIsLoading = dynamic(
    async () => {
        const { AuthenticationIsLoading: AuthenticationIsLoading } = await import(
            /* webpackChunkName: "AuthenticationIsLoading" */
            '../AuthenticationIsLoading'
        );
        return AuthenticationIsLoading;
    },
    { ssr: false }
);

/**
 * Wraps a given experience with an authentication gate which determines whether to kick off a login flow before
 * rendering the experience
 * @param WrappedComponent The experience to be protected with authentication
 * @param options Configure whether authentication should be required on this experience
 */
export const withAuth = <TProps extends object>(
    WrappedComponent: FC<TProps>,
    options: WithAuthConfiguration = DEFAULT_WITH_AUTH_OPTIONS
) => {
    const WithAuth: FC<ServerSideSessionProps & LoginHintProps & TProps> = ({
        ...props
    }: ServerSideSessionProps & LoginHintProps & TProps) => {
        const { forceAuth = DEFAULT_WITH_AUTH_OPTIONS.forceAuth } = options;

        const { emailLoginHint } = props;
        const { session, status } = useWithAuthState({
            forceAuth,
            emailLoginHint,
        });
        const notAuthenticated = !session || status !== SessionStatus.AUTHENTICATED;

        if (forceAuth && notAuthenticated) {
            return <AuthenticationIsLoading />;
        }
        if (forceAuth) {
            return (
                <SessionWatcher>
                    <WrappedComponent {...(props as TProps)} />
                </SessionWatcher>
            );
        }
        return <WrappedComponent {...(props as TProps)} />;
    };
    // This is necessary because NextJS runs in strict mode, and name is a readonly property
    Object.defineProperty(WithAuth, 'name', {
        value: `WithAuth(${getComponentDisplayName<TProps>(WrappedComponent)})`,
    });
    return WithAuth;
};
